import { useCallback, useEffect, useRef, useState } from 'react';
import { usePathname } from 'expo-router';
import { trpc } from '../lib/trpc';
import type { ObservabilityScalar } from '../lib/observability';
import {
  trackFireCalculationCompleted,
  trackFireCalculationFailed,
  trackFireCalculationRequested,
  trackFireCalculationStatusChanged,
} from '../lib/product-events';

type RecalcState = 'idle' | 'scheduled' | 'paused' | 'running' | 'error';

type UseNativeFireCalculationOptions = {
  queuedLabel: string;
  runningLabel: string;
  doneLabel: string;
  screen: 'onboarding' | 'fire-plan';
  source: 'onboarding' | 'fire_plan';
  metadata?: Record<string, ObservabilityScalar> | null;
};

function getMutationErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof Error && error.message) return error.message;
  return fallback;
}

export function useNativeFireCalculation(options: UseNativeFireCalculationOptions) {
  const pathname = usePathname();
  const utils = trpc.useUtils();
  const completionHandledRef = useRef(false);
  const lastReportedStatusRef = useRef<string | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);
  const [isDone, setIsDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [statusLabel, setStatusLabel] = useState(options.queuedLabel);
  const [requestedAtMs, setRequestedAtMs] = useState<number | null>(null);

  const statusQ = trpc.fire.getRecalcStatus.useQuery(undefined, {
    enabled: isCalculating,
    refetchInterval: (query) => {
      if (!isCalculating) return false;

      const state = (query.state.data as { state?: RecalcState } | undefined)?.state;
      if (state === 'running' || state === 'scheduled' || state === 'paused') return 1000;
      return 1500;
    },
  });

  const { refetch: refetchStatus, data: status } = statusQ;
  const runNowMutation = trpc.fire.runRecalcNow.useMutation({
    onSuccess: () => {
      void refetchStatus();
    },
  });

  const reset = useCallback(() => {
    completionHandledRef.current = false;
    lastReportedStatusRef.current = null;
    setIsCalculating(false);
    setIsDone(false);
    setError(null);
    setProgress(0);
    setStatusLabel(options.queuedLabel);
    setRequestedAtMs(null);
  }, [options.queuedLabel]);

  const start = useCallback(async () => {
    if (isCalculating || runNowMutation.isPending) return;

    const startedAt = Date.now();
    completionHandledRef.current = false;
    lastReportedStatusRef.current = 'queued';
    setIsCalculating(true);
    setIsDone(false);
    setError(null);
    setRequestedAtMs(startedAt);
    setProgress(0.12);
    setStatusLabel(options.queuedLabel);
    trackFireCalculationRequested({
      route: pathname,
      screen: options.screen,
      source: options.source,
      transport: 'recalc_queue',
      metadata: options.metadata,
    });
    trackFireCalculationStatusChanged({
      route: pathname,
      screen: options.screen,
      source: options.source,
      transport: 'recalc_queue',
      status: 'queued',
      progress: 0.12,
      metadata: options.metadata,
    });

    try {
      await runNowMutation.mutateAsync();
      await refetchStatus();
    } catch (mutationError) {
      const message = getMutationErrorMessage(
        mutationError,
        'Could not start FIRE calculation.',
      );
      trackFireCalculationFailed({
        route: pathname,
        screen: options.screen,
        source: options.source,
        transport: 'recalc_queue',
        durationMs: Date.now() - startedAt,
        errorMessage: message,
        metadata: options.metadata,
      });
      setError(message);
      setIsCalculating(false);
    }
  }, [
    isCalculating,
    options.metadata,
    options.queuedLabel,
    options.screen,
    options.source,
    pathname,
    refetchStatus,
    runNowMutation,
  ]);

  useEffect(() => {
    if (!isCalculating) return;

    const state = status?.state;

    if (state === 'scheduled' || state === 'paused' || state == null) {
      if (lastReportedStatusRef.current !== 'queued') {
        lastReportedStatusRef.current = 'queued';
        trackFireCalculationStatusChanged({
          route: pathname,
          screen: options.screen,
          source: options.source,
          transport: 'recalc_queue',
          status: 'queued',
          progress,
          metadata: options.metadata,
        });
      }
      setStatusLabel(options.queuedLabel);
      setProgress((current) => Math.max(current, 0.12));
      return;
    }

    if (state === 'running') {
      if (lastReportedStatusRef.current !== 'running') {
        lastReportedStatusRef.current = 'running';
        trackFireCalculationStatusChanged({
          route: pathname,
          screen: options.screen,
          source: options.source,
          transport: 'recalc_queue',
          status: 'running',
          progress,
          metadata: options.metadata,
        });
      }
      setStatusLabel(options.runningLabel);
      setProgress((current) => Math.max(current, 0.3));
      return;
    }

    if (state === 'error') {
      completionHandledRef.current = true;
      const message = status?.lastError ?? 'FIRE calculation failed.';
      trackFireCalculationFailed({
        route: pathname,
        screen: options.screen,
        source: options.source,
        transport: 'recalc_queue',
        durationMs:
          requestedAtMs != null ? Date.now() - requestedAtMs : null,
        errorMessage: message,
        metadata: options.metadata,
      });
      setError(message);
      setIsCalculating(false);
      return;
    }

    if (state === 'idle' && requestedAtMs != null && !completionHandledRef.current) {
      const completedAtMs = status?.lastCompletedAt
        ? new Date(status.lastCompletedAt).getTime()
        : null;

      if (completedAtMs != null && completedAtMs >= requestedAtMs - 1000) {
        completionHandledRef.current = true;
        setProgress(1);
        setStatusLabel(options.doneLabel);
        setIsCalculating(false);
        setIsDone(true);
        trackFireCalculationCompleted({
          route: pathname,
          screen: options.screen,
          source: options.source,
          transport: 'recalc_queue',
          durationMs: Date.now() - requestedAtMs,
          metadata: options.metadata,
        });
        void Promise.allSettled([
          utils.fire.getPlan.invalidate(),
          utils.fire.getLastRecalcImpact.invalidate(),
        ]);
      }
    }
  }, [
    isCalculating,
    options.doneLabel,
    options.metadata,
    options.queuedLabel,
    options.runningLabel,
    options.screen,
    options.source,
    pathname,
    progress,
    requestedAtMs,
    status?.lastCompletedAt,
    status?.lastError,
    status?.state,
    utils.fire.getLastRecalcImpact,
    utils.fire.getPlan,
  ]);

  useEffect(() => {
    if (!isCalculating) return;

    const intervalId = setInterval(() => {
      const state = status?.state;

      setProgress((current) => {
        if (state === 'running') {
          return Math.min(0.9, current + 0.025);
        }

        return Math.min(0.25, current + 0.01);
      });
    }, 800);

    return () => clearInterval(intervalId);
  }, [isCalculating, status?.state]);

  return {
    error,
    isCalculating,
    isDone,
    progress,
    reset,
    start,
    statusLabel,
  };
}
